/**
 * `Scrub` — the 56px waveform band, which **is** the scrub control.
 * `docs/design-direction.md` §9.5, §7.2 SCRUB LOUPE, §5.6.
 *
 * The target is the whole band, never the 1px playhead: a hairline is a mark, not a
 * handle, and a 44px-minimum rule that exempts "the important one" is not a rule.
 *
 * **The gesture safety contract, which is why this component exists at all.**
 * Telegram's webview reads a vertical drag inside a Mini App as "close me". A drag
 * that starts on the wave and wanders 8px down would therefore collapse the app
 * mid-scrub. Two things prevent it, and both are non-negotiable:
 *
 *  1. `touch-action: none` on the band (`components.module.css`), overriding the
 *     reset's `manipulation`, so the browser never steals the drag for a scroll;
 *  2. `disableVerticalSwipes()` on `pointerdown`, **paired with
 *     `enableVerticalSwipes()` on every single exit path** — `pointerup`,
 *     `pointercancel`, `lostpointercapture`, and unmount-while-dragging. If any one
 *     of those paths forgot, the user would be left in an app that cannot be closed
 *     by the gesture the platform trained them to use, with no way back. That is the
 *     regression the test suite asserts four times over.
 *
 * `setPointerCapture` keeps the stream on this element when the finger leaves the
 * band. It is an optional call: jsdom does not implement it and neither do the oldest
 * webviews, and the handlers are on the same element either way, so capture is an
 * improvement rather than a requirement.
 *
 * **Keyboard is a first-class path** (§9.5): `role="slider"` with a live
 * `aria-valuenow` in seconds and an `aria-valuetext` that speaks a timecode rather
 * than a number of seconds; ←/→ move 5s, ↑/↓ and Shift+←/→ move 30s, Home and End go
 * to the ends. `End` lands one second short of the duration on purpose — seeking to
 * exactly `duration` fires `ended` and skips the track, which is not what pressing
 * End means.
 *
 * The loupe (§7.2): while the thumb is down the band carries `data-loupe="true"` and
 * a mono timecode reads out at the playhead, set at `--fs-13` and *scaled* to the
 * 20px reading — a transform, because only transform, opacity and clip-path are ever
 * animated (§7.1) and an animated `font-size` would reflow the band under the finger.
 * The screen owns the other half of the move: everything except the wave, the ruler
 * and the timecode drops to `opacity: .4`, keyed off the same attribute.
 *
 * `impactOccurred('rigid')` fires once per one-drop ruler tick crossed — the band is
 * 24 ticks wide, so the drag feels like a hand moving across a scale rather than a
 * continuous smear.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX, KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';

import { telegram } from '../lib/telegram';

import styles from './components.module.css';
import { Waveform, waveProgressVars } from './Waveform';
import { cx } from './primitives/Rule';

/** ←/→ (§9.5). */
export const STEP_S = 5;
/** ↑/↓ and Shift+←/→ (§9.5). */
export const BIG_STEP_S = 30;
/** `Ruler`'s `scale` variant is 24 ticks; one rigid haptic per tick crossed (§7.2). */
const RULER_TICKS = 24;

/** The control's Russian name (§9.4's rule, applied to the one control §9.5 defines). */
export const SCRUB_LABEL = 'Перемотка';

/**
 * `M:SS`, or `H:MM:SS` past an hour. Tabular mono digits, no fabricated precision.
 *
 * It lives here because §9.5 makes this component the owner of the spoken timecode,
 * and `TrackRow`'s duration cell is the same mark in a different tier. One
 * implementation, so a duration and a scrub readout can never disagree by a rounding
 * rule.
 */
export function formatTimecode(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const ss = String(secs).padStart(2, '0');
  if (hours === 0) return `${String(minutes)}:${ss}`;
  return `${String(hours)}:${String(minutes).padStart(2, '0')}:${ss}`;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value <= min) return min;
  return value >= max ? max : value;
}

export interface ScrubProps {
  /** Effective duration in seconds. `0` disables seeking without hiding the band. */
  readonly durationS: number;
  /** The playhead position in seconds. */
  readonly positionS: number;
  /** Server peaks, or `null` for the designed no-peaks band (§6.7). */
  readonly peaks: Uint8Array | null;
  /** Commit. Called on `pointerup` and on every keyboard move. */
  readonly onSeek: (seconds: number) => void;
  /** The band entered the loupe state. */
  readonly onScrubStart?: (() => void) | undefined;
  /** The band left it — on commit, cancel or lost capture alike. */
  readonly onScrubEnd?: (() => void) | undefined;
  /** §13.5's honesty rule: a stream that is not fully seekable cannot be dragged. */
  readonly disabled?: boolean;
  readonly label?: string;
  readonly className?: string | undefined;
}

export function Scrub({
  durationS,
  positionS,
  peaks,
  onSeek,
  onScrubStart,
  onScrubEnd,
  disabled = false,
  label = SCRUB_LABEL,
  className,
}: ScrubProps): JSX.Element {
  const bandRef = useRef<HTMLDivElement | null>(null);
  /** `null` when no drag is in flight; otherwise the last ruler tick a haptic fired on. */
  const dragRef = useRef<{ tick: number } | null>(null);
  /** The value the next commit will send, kept out of state so no handler reads a stale one. */
  const valueRef = useRef(0);
  const [preview, setPreview] = useState<number | null>(null);

  const duration = Number.isFinite(durationS) && durationS > 0 ? durationS : 0;
  const shown = preview ?? clamp(positionS, 0, duration);
  const progress = duration > 0 ? shown / duration : 0;
  const dragging = preview !== null;
  const inert = disabled || duration <= 0;

  /*
   * The commit value is mirrored into a ref after every render rather than read out
   * of a closure: a pointer handler created three renders ago would otherwise seek to
   * a position three renders stale. A ref is written in an effect, never during
   * render, because a ref is not a rendering input.
   */
  useEffect(() => {
    valueRef.current = shown;
  }, [shown]);

  /* ── the safety net: a drag that outlives its component still re-enables ──── */

  useEffect(
    () => () => {
      if (dragRef.current !== null) {
        dragRef.current = null;
        telegram.enableVerticalSwipes();
      }
    },
    [],
  );

  const valueFromPointer = useCallback(
    (clientX: number): number | null => {
      const band = bandRef.current;
      if (band === null || duration <= 0) return null;
      const rect = band.getBoundingClientRect();
      /* A zero-width box means the band has not been laid out (or this is jsdom):
         there is no position to read, so the drag holds its last value rather than
         jumping to zero. */
      if (rect.width <= 0) return null;
      return clamp(((clientX - rect.left) / rect.width) * duration, 0, duration);
    },
    [duration],
  );

  const tickOf = useCallback(
    (seconds: number): number => {
      if (duration <= 0) return 0;
      return Math.min(RULER_TICKS - 1, Math.floor((seconds / duration) * RULER_TICKS));
    },
    [duration],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      if (inert || dragRef.current !== null) return;

      /* Order matters: Telegram's gesture is disabled BEFORE anything can move. */
      telegram.disableVerticalSwipes();
      event.currentTarget.setPointerCapture?.(event.pointerId);

      const value = valueFromPointer(event.clientX) ?? valueRef.current;
      dragRef.current = { tick: tickOf(value) };
      valueRef.current = value;
      setPreview(value);
      onScrubStart?.();
    },
    [inert, onScrubStart, tickOf, valueFromPointer],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>): void => {
      const drag = dragRef.current;
      if (drag === null) return;

      const value = valueFromPointer(event.clientX);
      if (value === null) return;

      valueRef.current = value;
      setPreview(value);

      const tick = tickOf(value);
      if (tick !== drag.tick) {
        drag.tick = tick;
        telegram.haptics.impact('rigid');
      }
    },
    [tickOf, valueFromPointer],
  );

  /**
   * Every exit path funnels through here, and `commit` is the only difference between
   * them: a release seeks, a cancelled or stolen pointer abandons the drag and lets
   * the real position take the band back. Whichever it is, the vertical-swipe gesture
   * is handed back to Telegram.
   */
  const endDrag = useCallback(
    (commit: boolean): void => {
      if (dragRef.current === null) return;
      dragRef.current = null;
      telegram.enableVerticalSwipes();

      const value = valueRef.current;
      setPreview(null);
      if (commit) onSeek(value);
      onScrubEnd?.();
    },
    [onScrubEnd, onSeek],
  );

  const onPointerUp = useCallback((): void => {
    endDrag(true);
  }, [endDrag]);

  const onPointerCancel = useCallback((): void => {
    endDrag(false);
  }, [endDrag]);

  const onLostPointerCapture = useCallback((): void => {
    endDrag(false);
  }, [endDrag]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (inert) return;

      const from = valueRef.current;
      let next: number | null = null;

      switch (event.key) {
        case 'ArrowRight':
          next = from + (event.shiftKey ? BIG_STEP_S : STEP_S);
          break;
        case 'ArrowLeft':
          next = from - (event.shiftKey ? BIG_STEP_S : STEP_S);
          break;
        case 'ArrowUp':
          next = from + BIG_STEP_S;
          break;
        case 'ArrowDown':
          next = from - BIG_STEP_S;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          /* One second short: seeking to exactly `duration` ends the track. */
          next = Math.max(0, duration - 1);
          break;
        default:
          return;
      }

      event.preventDefault();
      onSeek(clamp(next, 0, duration));
    },
    [duration, inert, onSeek],
  );

  return (
    <div
      ref={bandRef}
      className={cx(styles.band, className)}
      style={waveProgressVars(progress)}
      role="slider"
      tabIndex={inert ? -1 : 0}
      aria-label={label}
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(shown)}
      aria-valuetext={`${formatTimecode(shown)} из ${formatTimecode(duration)}`}
      aria-disabled={inert ? true : undefined}
      data-loupe={dragging ? 'true' : 'false'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={onLostPointerCapture}
      onKeyDown={onKeyDown}
    >
      <Waveform peaks={peaks} progress={progress} />
      {dragging ? (
        /* Hidden from assistive technology because `aria-valuetext` already speaks
           it — the loupe is the same fact, printed for the eye. */
        <span className={cx('t-mono', 't-13', styles.loupe)} aria-hidden="true">
          {formatTimecode(shown)}
        </span>
      ) : null}
    </div>
  );
}
